import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowLeft, Pencil, FileText, LayoutTemplate, QrCode } from 'lucide-react';
import { paths } from '../../routes/paths';
import { getError } from '../../utils';
import { downloadRifaTicketPreview } from '../../utils/downloadRifaTicketPreview';
import rifaService from '../../services/rifa.service';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import LoadingBox from '../../components/LoadingBox';
import MessageBox from '../../components/MessageBox';
import StatusLoadingOverlay from '../../components/StatusLoadingOverlay';
import RifaQrDialog from '../../components/RifaQrDialog';
import {
  computeBoardTotalTickets,
  isSharedBoard,
  mostrarNumerosOf,
  pickerModeOf,
  sharedBoardSize,
  sorteosEnabledOf,
} from '../../lib/boardPlans';

const PREVIEW_STATUS_KEYS = [
  'reservation.ticketDownloadPreparing',
  'reservation.ticketDownloadGenerating',
  'reservation.ticketDownloadRendering',
  'reservation.ticketDownloadDownloading',
];

/** Small labelled field for read-only display */
function Field({ label, value }) {
  return (
    <div className='space-y-1'>
      <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>{label}</p>
      <p className='text-sm text-foreground'>{value || '—'}</p>
    </div>
  );
}

export default function RifaDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rifa, setRifa] = useState(null);
  const [previewOverlay, setPreviewOverlay] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await rifaService.getRifa(id);
        setRifa(data);
      } catch (err) {
        setError(getError(err));
        toast.error(getError(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const isBoard = rifa?.tipo === 'board';
  const categories = rifa?.config?.categories || [];

  const shared = isSharedBoard(rifa);
  const sorteosOn = sorteosEnabledOf(rifa);
  const computedBoardTickets = useMemo(
    () => computeBoardTotalTickets(rifa?.config),
    [rifa?.config]
  );

  if (loading) return <LoadingBox />;
  if (error) return <MessageBox variant='danger'>{error}</MessageBox>;
  if (!rifa) return <MessageBox variant='danger'>{t('common.noResults')}</MessageBox>;

  const handlePreviewTicket = async () => {
    setPreviewOverlay(true);
    try {
      await downloadRifaTicketPreview(id);
    } catch (err) {
      toast.error(getError(err) || t('raffle.previewTicketError'));
    } finally {
      setPreviewOverlay(false);
    }
  };

  return (
    <div className='space-y-6'>
      {previewOverlay && <StatusLoadingOverlay messageKeys={PREVIEW_STATUS_KEYS} />}
      <RifaQrDialog rifa={rifa} open={qrOpen} onOpenChange={setQrOpen} />
      <PageHeader
        title={`${t('raffle.raffle')} — ${rifa.nombre || ''}`}
        subtitle={rifa.descripcion || ''}
        actions={
          <div className='flex flex-wrap gap-2'>
            <Button variant='outline' onClick={() => navigate('/AdminScreen/rifas')}>
              <ArrowLeft className='h-4 w-4' />
              {t('common.back')}
            </Button>
            <Button variant='outline' disabled={previewOverlay} onClick={handlePreviewTicket}>
              <FileText className='h-4 w-4' />
              {t('raffle.previewTicket')}
            </Button>
            <Button variant='outline' onClick={() => setQrOpen(true)}>
              <QrCode className='h-4 w-4' />
              {t('raffle.qrButton', 'Código QR')}
            </Button>
            <Button variant='outline' onClick={() => navigate(paths.admin.rifaTicketDesign(id))}>
              <LayoutTemplate className='h-4 w-4' />
              {t('ticketDesign.link')}
            </Button>
            <Button onClick={() => navigate(`/AdminScreen/editRifa/${id}`)}>
              <Pencil className='h-4 w-4' />
              {t('common.edit')}
            </Button>
          </div>
        }
      />

      {/* Main info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('common.information')}</CardTitle>
        </CardHeader>
        <CardContent className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          <Field label={t('common.name')} value={rifa.nombre} />
          <Field label={t('common.description')} value={rifa.descripcion} />
          <div className='space-y-1'>
            <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              {t('raffle.type')}
            </p>
            <div className='flex items-center gap-2'>
              <Badge variant={isBoard ? 'info' : 'secondary'}>
                {isBoard ? t('raffle.typeBoard') : t('raffle.typeStandard')}
              </Badge>
            </div>
          </div>

          <Field
            label={t('raffle.sorteosEnabled', 'Sorteos')}
            value={sorteosOn ? t('common.yes', 'Sí') : t('common.no', 'No')}
          />
          <Field
            label={t('raffle.pickerMode', 'Selección de números')}
            value={
              {
                manual: t('raffle.pickerModeManual', 'Manual'),
                random: t('raffle.pickerModeRandom', 'Aleatoria'),
                both: t('raffle.pickerModeBoth', 'Ambas'),
              }[pickerModeOf(rifa)]
            }
          />
          <Field
            label={t('raffle.mostrarNumeros', 'Mostrar números al comprador')}
            value={mostrarNumerosOf(rifa) ? t('common.yes', 'Sí') : t('common.no', 'No')}
          />

          {!isBoard && (
            <>
              <Field label={t('raffle.ticketQuantity')} value={String(rifa.totalTickets ?? 0)} />
              <Field label={t('raffle.ticketPrice')} value={`$${rifa.precioTicket ?? 0}`} />
            </>
          )}

          {isBoard && (
            <div className='space-y-1'>
              <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                {t('raffle.totalTickets')}
              </p>
              <p className='text-sm text-foreground'>
                {computedBoardTickets > 0 ? computedBoardTickets : (rifa.totalTickets ?? 0)}
              </p>
              <p className='text-xs text-muted-foreground'>{t('raffle.boardCategories')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Images */}
      <Card>
        <CardHeader>
          <CardTitle>Imágenes</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-wrap gap-6'>
          <div className='space-y-1'>
            <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              {t('raffle.raffleImage')}
            </p>
            <img
              alt={t('raffle.raffleImage')}
              src={rifa.rifaImage?.fileLink || '/images/emptyPhoto.png'}
              className='h-24 w-24 rounded-lg object-cover border border-border'
            />
          </div>
          <div className='space-y-1'>
            <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              {t('raffle.ticketImage')}
            </p>
            <img
              alt={t('raffle.ticketImage')}
              src={rifa.ticketImage?.fileLink || '/images/emptyPhoto.png'}
              className='h-24 w-24 rounded-lg object-cover border border-border'
            />
          </div>
          {isBoard && rifa.imagenTableroVendido?.fileLink && (
            <div className='space-y-1'>
              <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                {t('raffle.boardSoldSquareImage')}
              </p>
              <img
                alt=''
                src={rifa.imagenTableroVendido.fileLink}
                className='h-24 w-40 rounded-lg object-cover border border-border'
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Board categories */}
      {isBoard && categories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {shared ? t('raffle.plans', 'Planes') : t('raffle.boardCategories')}
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {shared && (
              <p className='text-sm text-muted-foreground'>
                {t('raffle.boardModeShared', 'Dentro de un mismo tablero')}:{' '}
                {sharedBoardSize(rifa.config).filas}×{sharedBoardSize(rifa.config).columnas} ={' '}
                {sharedBoardSize(rifa.config).filas * sharedBoardSize(rifa.config).columnas}{' '}
                {t('raffle.squares')}
              </p>
            )}
            {categories.map((cat, index) => (
              <div
                key={index}
                className='flex items-center gap-3 rounded-lg border border-border p-3'
              >
                <span
                  className='h-5 w-5 flex-shrink-0 rounded border border-border'
                  style={{ backgroundColor: cat.color || '#ccc' }}
                />
                <div className='min-w-0 flex-1'>
                  <p className='text-sm font-medium text-foreground'>{cat.nombre}</p>
                  {!shared && (
                    <p className='text-xs text-muted-foreground'>
                      {cat.filas}×{cat.columnas} = {cat.filas * cat.columnas} {t('raffle.squares')}
                    </p>
                  )}
                </div>
                <div className='flex gap-1'>
                  <Badge variant='secondary'>${cat.precioCasilla}/sq</Badge>
                  <Badge variant='info'>
                    {cat.ticketsPorCasilla} ticket{cat.ticketsPorCasilla !== 1 ? 's' : ''}/sq
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Sorteos */}
      {sorteosOn && (
        <Card>
          <CardHeader>
            <CardTitle>{t('raffle.sorteos')}</CardTitle>
          </CardHeader>
          <CardContent>
            {rifa.sorteos && rifa.sorteos.length > 0 ? (
              <div className='space-y-4'>
                {rifa.sorteos.map((sorteo, index) => (
                  <div key={sorteo._id || index} className='rounded-lg border border-border p-4'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <h5 className='font-medium text-foreground'>{sorteo.titulo}</h5>
                      {sorteo._id && sorteo.premios?.length > 0 && (
                        <Button
                          size='sm'
                          onClick={() =>
                            navigate(`/admin/sorteo-en-vivo?rifa=${rifa._id}&sorteo=${sorteo._id}`)
                          }
                        >
                          {t('liveDraw.drawAction')}
                        </Button>
                      )}
                    </div>
                    <div className='mt-2 grid gap-2 text-sm sm:grid-cols-2'>
                      <div>
                        <span className='text-muted-foreground'>{t('common.date')}: </span>
                        <span>{sorteo.fecha ? new Date(sorteo.fecha).toLocaleString() : '—'}</span>
                      </div>
                      <div>
                        <span className='text-muted-foreground'>{t('raffle.salesTarget')}: </span>
                        <span>{sorteo.objetivoVenta ?? '—'}</span>
                      </div>
                    </div>
                    {sorteo.premios && sorteo.premios.length > 0 && (
                      <div className='mt-3'>
                        <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2'>
                          {t('raffle.prizes')}
                        </p>
                        <div className='space-y-1'>
                          {sorteo.premios.map((premio, pi) => (
                            <div key={premio?._id || pi} className='text-sm'>
                              <span className='font-medium'>{premio?.puesto}º</span>{' '}
                              <span>{premio?.nombre}</span>
                              {premio?.descripcion && (
                                <span className='text-muted-foreground'>
                                  {' '}
                                  — {premio.descripcion}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(!sorteo.premios || sorteo.premios.length === 0) && (
                      <p className='mt-2 text-xs text-muted-foreground'>{t('raffle.noPrizes')}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-sm text-muted-foreground'>{t('raffle.noSorteos')}</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
